import React, { useEffect, useRef } from 'react'; // 👈 引入 useRef 用于滚动
import SunCalc from 'suncalc';

// ==========================================
// 🤫 秘密后台页面 (黄金罗盘版：自动高亮+滚动)
// ==========================================
const SecretPage = ({ onClose, location, date }) => {
  const { latitude, longitude } = location;
  
  // 1. 获取天文数据
  const times = SunCalc.getTimes(date, latitude, longitude);
  const tomorrow = new Date(date);
  tomorrow.setDate(date.getDate() + 1);
  const timesTomorrow = SunCalc.getTimes(tomorrow, latitude, longitude);

  // 辅助：格式化时间
  const formatTime = (dateObj) => {
    if (!dateObj || isNaN(dateObj.getTime())) return '---';
    return dateObj.toLocaleTimeString('zh-CN', { 
      hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' 
    });
  };

  const getDayLength = () => {
    if (!times.sunrise || !times.sunset) return '---';
    const diffMs = times.sunset - times.sunrise; 
    const diffMins = Math.floor(diffMs / 60000); 
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    const secs = Math.floor((diffMs % 60000) / 1000); 
    return `${hours}小时${mins}分${secs}秒`;
  };

  const SHI_CHEN = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];
  const KE_SUFFIXES = ['时初', '初一刻', '初二刻', '初三刻', '时正', '正一刻', '正二刻', '正三刻'];

  // ==========================================
  // 🧮 核心算法
  // ==========================================
  const MAO_ZHENG_INDEX = 28; 
  const YOU_ZHENG_INDEX = 76; 

  let dayStepMs = 0;
  if (times.sunrise && times.sunset) {
    dayStepMs = (times.sunset.getTime() - times.sunrise.getTime()) / 48;
  }

  let nightStepMs = 0;
  if (times.sunset && timesTomorrow.sunrise) {
    nightStepMs = (timesTomorrow.sunrise.getTime() - times.sunset.getTime()) / 48;
  }

  const calculateTimeForIndex = (globalIndex) => {
    if (globalIndex >= MAO_ZHENG_INDEX && globalIndex <= YOU_ZHENG_INDEX) {
      const offset = (globalIndex - MAO_ZHENG_INDEX) * dayStepMs;
      return new Date(times.sunrise.getTime() + offset);
    }
    if (globalIndex > YOU_ZHENG_INDEX) {
      const offset = (globalIndex - YOU_ZHENG_INDEX) * nightStepMs;
      return new Date(times.sunset.getTime() + offset);
    }
    if (globalIndex < MAO_ZHENG_INDEX) {
      const offset = (MAO_ZHENG_INDEX - globalIndex) * nightStepMs;
      return new Date(times.sunrise.getTime() - offset);
    }
    return null;
  };

  // 🚀 新增步骤：预先生成所有 96 个节点的数据，方便后面查找
  const allNodes = [];
  for (let i = 0; i < 96; i++) {
    const shiIndex = Math.floor(i / 8);
    const keIndex = i % 8;
    const name = `${SHI_CHEN[shiIndex]}${KE_SUFFIXES[keIndex]}`;
    const time = calculateTimeForIndex(i);
    allNodes.push({ index: i, name, time, shiIndex, keIndex });
  }

  // 🎯 核心逻辑：找到“当前时间”对应的是哪一个格子
  // 逻辑：遍历所有节点，找到最后一个“开始时间早于当前时间”的节点
  let activeIndex = -1;
  const currentTimestamp = date.getTime();
  
  // 我们倒着找，找到第一个比当前时间小的，那就是它了！
  for (let i = allNodes.length - 1; i >= 0; i--) {
    if (allNodes[i].time && allNodes[i].time.getTime() <= currentTimestamp) {
      activeIndex = i;
      break;
    }
  }
  // 特殊情况：如果是第二天凌晨（比如0点多），可能比列表里第一个时间还小，那时候应该算在前一天的最后，或者当天的开头
  // 为了简单，如果没找到，默认是0（子时初刻）
  if (activeIndex === -1) activeIndex = 0;


  // 📜 自动滚动逻辑
  useEffect(() => {
    const activeElement = document.getElementById(`node-${activeIndex}`);
    if (activeElement) {
      activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeIndex]); // 只要 activeIndex 变了（比如你拖动时间条），就滚过去


  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: '#0d0d0d', color: '#ccc', zIndex: 9999,
      display: 'flex', flexDirection: 'column', 
      fontFamily: '"Ma Shan Zheng", monospace', 
      overflow: 'hidden' 
    }}>
      
      {/* 顶部标题栏 */}
      <div style={{ 
        padding: '15px', borderBottom: '1px solid #333', background: '#111',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <h2 style={{ margin: 0, color: '#fff', fontSize: '18px' }}>📜 96刻度时空表</h2>
        <button 
          onClick={onClose}
          style={{ padding: '6px 12px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: '4px', cursor: 'pointer' }}
        >
          关闭
        </button>
      </div>

      {/* 天文参数面板 */}
      <div style={{
        padding: '15px', background: 'rgba(255, 255, 255, 0.05)', borderBottom: '1px solid #444',
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px'
      }}>
        <div><span style={{color:'#aaa'}}>🌅 日出:</span> <span style={{color:'#ff4d4f'}}>{formatTime(times.sunrise)}</span></div>
        <div><span style={{color:'#aaa'}}>🌇 日落:</span> <span style={{color:'#ff4d4f'}}>{formatTime(times.sunset)}</span></div>
        <div><span style={{color:'#aaa'}}>☀️ 日中:</span> <span style={{color:'#ffa500'}}>{formatTime(times.solarNoon)}</span></div>
        <div><span style={{color:'#aaa'}}>⏳ 昼长:</span> <span style={{color:'#4ade80'}}>{getDayLength()}</span></div>
      </div>

      {/* 表格容器 */}
      <div style={{ flex: 1, overflow: 'auto', padding: '10px' }}>
        <div style={{ 
          display: 'grid', gridTemplateColumns: 'repeat(8, minmax(80px, 1fr))', 
          borderTop: '1px solid #444', borderLeft: '1px solid #444',
        }}>
          
          {allNodes.map((node) => {
            const isKeyNode = node.keIndex === 0 || node.keIndex === 4;
            const isMaoOrYou = node.index === MAO_ZHENG_INDEX || node.index === YOU_ZHENG_INDEX;
            const isActive = node.index === activeIndex; // 是否是当前时刻

            // 🎨 动态配色逻辑
            let bgColor = 'transparent';
            let nameColor = isKeyNode ? '#ff4d4f' : '#ddd';
            let timeColor = '#666';

            // 1. 基础背景
            if (isKeyNode) bgColor = 'rgba(255, 77, 79, 0.1)';

            // 2. 日出日落特殊色
            if (isMaoOrYou) {
               timeColor = '#00ff00';
            } else if (node.index > MAO_ZHENG_INDEX && node.index < YOU_ZHENG_INDEX) {
               timeColor = '#ccc'; // 白天
            }

            // 3. 🏆 黄金高亮（覆盖上面的颜色）
            if (isActive) {
              bgColor = 'linear-gradient(135deg, #ffd700 0%, #ffa500 100%)'; // 金色渐变
              nameColor = '#000'; // 黑字
              timeColor = '#000'; // 黑字
            }

            return (
              <div 
                key={node.index} 
                id={`node-${node.index}`} // 给每个格子一个ID，方便滚动定位
                style={{
                  borderRight: '1px solid #444', borderBottom: '1px solid #444',
                  minHeight: '60px', display: 'flex', flexDirection: 'column',
                  background: bgColor,
                  transition: 'background 0.3s ease', // 加个过渡动画
                  // 如果是高亮，加个阴影让它浮起来
                  boxShadow: isActive ? '0 0 15px rgba(255, 215, 0, 0.5)' : 'none',
                  zIndex: isActive ? 1 : 0
                }}
              >
                <div style={{ 
                  padding: '4px', fontSize: '14px', fontWeight: 'bold', 
                  color: nameColor, borderBottom: '1px dashed #333' 
                }}>
                  {node.name}
                </div>
                <div style={{ 
                  flex: 1, padding: '4px', fontSize: '12px', 
                  color: timeColor, display: 'flex', alignItems: 'center', fontWeight: isActive ? 'bold' : 'normal'
                }}>
                  {node.time ? formatTime(node.time) : ''}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: '20px', textAlign: 'center', color: '#555', fontSize: '12px' }}>← 左右滑动查看完整表格 →</div>
      </div>
    </div>
  );
};

export default SecretPage;